/** One non-overlapping refresh loop; hidden pages catch up immediately on return. */
export function startVisiblePolling(options: {
  poll: () => Promise<void>;
  delay: () => number;
  onError: (error: unknown) => void;
}) {
  let stopped = false;
  let running = false;
  let requested = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const visible = () => typeof document === 'undefined' || !document.hidden;
  const clear = () => { if (timer !== undefined) clearTimeout(timer); timer = undefined; };
  const schedule = () => {
    clear();
    if (!stopped && !running && visible()) timer = setTimeout(() => void refresh(), options.delay());
  };
  const refresh = async () => {
    clear();
    if (stopped || !visible()) return;
    if (running) { requested = true; return; }
    running = true;
    try { await options.poll(); } catch (error) { if (!stopped) options.onError(error); }
    finally {
      running = false;
      if (requested && !stopped) { requested = false; void refresh(); }
      else schedule();
    }
  };
  const onVisibility = () => { if (visible()) void refresh(); else clear(); };
  document.addEventListener('visibilitychange', onVisibility);
  void refresh();
  return {
    refresh,
    reschedule: schedule,
    stop() { stopped = true; clear(); document.removeEventListener('visibilitychange', onVisibility); },
  };
}
